import './PlaceCard.css';
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { Card } from '../../components/Card';

/** Cartões de lugar do §8.1: a classe é `place-card--{kind}` e o `data-testid`, `{kind}-card` (§8.1 bloco 2). */
export type PlaceKind = 'uf' | 'municipality' | 'abroad' | 'country' | 'abroad-city';

/** O `h2` do `PlaceHeader` (§8.1): nome acessível do cartão e alvo de reserva do foco (§15.2.2 regra 4). */
export const PLACE_TITLE_ID = 'place-title';

export interface PlaceCardProps {
  kind: PlaceKind;
  children?: ComponentChildren;
  /** Classes extras do dono (ex.: "uf-card", níveis `place-card--trim-{n}` do §8.7). */
  class?: string;
  /** Raiz do ajuste progressivo (§8.7, `data-trim-root`): só o `UfCard`. */
  trimRoot?: boolean;
  /** Estado exibido (`data-state`), para testes e estilos do dono. */
  state?: string;
}

/**
 * Estrutura comum dos cartões de lugar (§8.1): `Card` grow, `<section aria-labelledby="place-title">` (o `h2` do
 * `PlaceHeader`), pilha com gap 14 entre `UpLink`, cabeçalho e seções. Os `UfNavButtons` ficam no canto (absolutos sobre
 * o `.card`, que já é `position: relative`). Posição e altura de cada modo: PlaceCard.css (§8.1, §8.8).
 *
 * Foco (§15.2.2 regra 4): uma atualização que remove o elemento focado dentro do cartão (os nomes de antes dos votos
 * viram linhas de candidato às 17h; o duelo sem votos vira o duelo) leva o foco ao título do cartão, nunca ao `body`. O
 * elemento focado é lido no render, antes do diff (o DOM ainda é o anterior), e conferido depois do commit.
 */
export function PlaceCard({ kind, children, class: extra, trimRoot = false, state }: PlaceCardProps) {
  const focused = useRef<Element | null>(null);
  if (typeof document !== 'undefined') {
    const active = document.activeElement;
    const root = document.getElementById(PLACE_TITLE_ID)?.closest('.place-card') ?? null;
    focused.current = active && root?.contains(active) ? active : null;
  }
  useLayoutEffect(() => {
    const el = focused.current;
    focused.current = null;
    if (!el || el.isConnected) return;
    const now = document.activeElement;
    if (now && now !== document.body) return;
    document.getElementById(PLACE_TITLE_ID)?.focus({ preventScroll: true });
  });

  const cls = ['place-card', `place-card--${kind}`, extra].filter(Boolean).join(' ');
  return (
    <Card
      variant="grow"
      class={cls}
      data-testid={`${kind}-card`}
      data-state={state}
      data-trim-root={trimRoot ? '' : undefined}
      labelledBy={PLACE_TITLE_ID}
    >
      {children}
    </Card>
  );
}
